import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { FileText, Lock, Plus, Send, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Input } from "@/components/ui/Input";
import { DurationInput } from "@/components/ui/DurationInput";
import { Select } from "@/components/ui/Select";
import { Textarea } from "@/components/ui/Textarea";
import { SearchableCombobox } from "@/components/ui/SearchableCombobox";
import { getDataAdapter } from "@/data";
import { emitDataEvent } from "@/data/events";
import { rateHint } from "@/utils/rates";
import { durationMinutes, durationUnit, durationRule, formatDuration, roundDurationMinutes } from "@/utils/duration";
import { useAuth } from "@/auth/AuthProvider";
const regardingOptions = [
    { value: "account", label: "Account" },
    { value: "msdyn_workorder", label: "Work Order" },
    { value: "msdyn_project", label: "Project" },
];
// Mirrors the OOB msdyn_timeentrytype global choice (see EntryType / TYPE_TO_CODE).
const typeOptions = [
    { value: "Work", label: "Work" },
    { value: "Travel", label: "Travel" },
    { value: "On Break", label: "On Break" },
    { value: "Overtime", label: "Overtime" },
    { value: "Absence", label: "Absence" },
    { value: "Vacation", label: "Vacation" },
];
export function NewEntryDialog({ open, onClose, onSaved, onDeleted, initial, staffResources }) {
    const adapter = getDataAdapter();
    const { user } = useAuth();
    const isStaffMode = staffResources !== undefined;
    const today = format(new Date(), "yyyy-MM-dd");
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [date, setDate] = useState(today);
    const [startTime, setStartTime] = useState("09:00");
    const [minutes, setMinutes] = useState(60);
    const [durationEdited, setDurationEdited] = useState(false);
    const [lookupsLoaded, setLookupsLoaded] = useState(false);
    const [relatedLoading, setRelatedLoading] = useState(false);
    const [relatedError, setRelatedError] = useState(null);
    const [error, setError] = useState(null);
    const [description, setDescription] = useState("");
    const [regardingType, setRegardingType] = useState("account");
    const [regardingId, setRegardingId] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [bookingId, setBookingId] = useState(null);
    const [tcId, setTcId] = useState(null);
    const [roleId, setRoleId] = useState(null);
    const [entryType, setEntryType] = useState("Work");
    const [internalDescription, setInternalDescription] = useState("");
    const [externalComments, setExternalComments] = useState("");
    const [resourceId, setResourceId] = useState("");
    const [accounts, setAccounts] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [projects, setProjects] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [transCats, setTransCats] = useState([]);
    const [resCats, setResCats] = useState([]);
    useEffect(() => {
        if (!open)
            return;
        setLookupsLoaded(false);
        setError(null);
        let alive = true;
        void Promise.all([
            adapter.listAccounts(!initial),
            adapter.listWorkOrders(),
            adapter.listProjects(),
            adapter.listTransactionCategories(),
            adapter.listResourceCategories(),
        ]).then(([a, w, p, tc, rc]) => {
            if (!alive)
                return;
            setAccounts(a);
            setWorkOrders(w);
            setProjects(p);
            setTransCats(tc);
            setResCats(rc);
            setLookupsLoaded(true);
        }).catch((err) => {
            if (!alive)
                return;
            console.error("Entry lookups failed", err);
            setError(err instanceof Error ? err.message : String(err));
        });
        return () => { alive = false; };
    }, [adapter, open, initial]);
    useEffect(() => {
        if (!open)
            return;
        if (initial) {
            const s = new Date(initial.msdyn_start);
            setDate(format(s, "yyyy-MM-dd"));
            setStartTime(format(s, "HH:mm"));
            setMinutes(durationMinutes(initial));
            setDescription(initial.msdyn_description);
            setRegardingType(initial.regardingobjectid?.type ?? "account");
            setRegardingId(initial.regardingobjectid?.id ?? null);
            setTaskId(initial.msdyn_projecttaskid ?? null);
            setBookingId(initial.msdyn_bookableresourcebooking ?? null);
            setTcId(initial.msdyn_transactioncategoryid ?? null);
            setRoleId(initial.resourceCategoryId ?? null);
            setEntryType(initial.msdyn_type);
            setInternalDescription(initial.crb7a_detailsofwork ?? "");
            setExternalComments(initial.msdyn_externaldescription ?? "");
        }
        else {
            setDate(today);
            setStartTime("09:00");
            setMinutes(60);
            setDescription("");
            setRegardingType("account");
            setRegardingId(null);
            setTaskId(null);
            setBookingId(null);
            setTcId(null);
            setRoleId(null);
            setEntryType("Work");
            setInternalDescription("");
            setExternalComments("");
        }
        setDurationEdited(false);
        setResourceId(initial?.bookableresource ?? (isStaffMode ? "" : user.bookableResourceId));
    }, [initial, open, today, isStaffMode, user.bookableResourceId]);
    useEffect(() => {
        if (!open)
            return;
        let alive = true;
        setTasks([]);
        setBookings([]);
        setRelatedError(null);
        setRelatedLoading(true);
        void Promise.all([
            regardingType === "msdyn_project" && regardingId ? adapter.listProjectTasks(regardingId) : Promise.resolve([]),
            regardingType === "msdyn_workorder" && regardingId ? adapter.listBookings(regardingId) : Promise.resolve([]),
        ]).then(([projectTasks, appointments]) => {
            if (!alive)
                return;
            setTasks(projectTasks);
            setBookings(appointments);
        }).catch((err) => {
            if (!alive)
                return;
            console.error("Related entry lookups failed", err);
            setRelatedError(err instanceof Error ? err.message : String(err));
        }).finally(() => { if (alive)
            setRelatedLoading(false); });
        return () => { alive = false; };
    }, [adapter, regardingId, regardingType, open]);
    const regardingItems = useMemo(() => {
        if (regardingType === "account")
            return accounts.map((a) => ({ value: a.accountid, label: a.name, hint: a.industry }));
        if (regardingType === "msdyn_workorder")
            return workOrders.map((w) => ({
                value: w.msdyn_workorderid,
                label: w.msdyn_name,
                hint: accounts.find((a) => a.accountid === w.msdyn_serviceaccount)?.name,
            }));
        return projects.map((p) => ({
            value: p.msdyn_projectid,
            label: p.msdyn_subject,
            hint: accounts.find((a) => a.accountid === p.msdyn_customer)?.name,
        }));
    }, [regardingType, accounts, workOrders, projects]);
    // Approved entries are read-only to the submitter. Manager must return the
    // entry first (which flips it to Returned), then the submitter can edit and
    // resubmit.
    const isLocked = initial?.msdyn_entryStatus === "Approved";
    // Editing a Submitted or Returned entry knocks it back to Draft so the
    // approver isn't reviewing stale data — they'll see it again next submit cycle.
    const nextStatus = (() => {
        if (!initial)
            return "Draft";
        if (initial.msdyn_entryStatus === "Submitted" || initial.msdyn_entryStatus === "Returned") {
            return "Draft";
        }
        return initial.msdyn_entryStatus;
    })();
    // Draft and Returned entries get a dedicated submit action so a single entry
    // can go to the approver without opening the weekly grid and submitting the
    // whole week. A Returned entry is a *re*-submit (hence the adapted label), and
    // its plain Save still drops it back to Draft.
    const isReturned = !isLocked && initial?.msdyn_entryStatus === "Returned";
    const canSubmitFromDialog = !isLocked &&
        (initial?.msdyn_entryStatus === "Draft" || initial?.msdyn_entryStatus === "Returned"
            || (!!staffResources && initial?.msdyn_entryStatus === "Submitted"));
    const submitLabel = staffResources && initial?.msdyn_entryStatus === "Submitted"
        ? "Save and resubmit"
        : isReturned ? "Resubmit for approval" : "Submit for approval";
    const unit = durationUnit(tcId, transCats);
    const validDuration = Number.isFinite(minutes) && minutes > 0;
    const savedMinutes = validDuration && (!initial || durationEdited)
        ? roundDurationMinutes(minutes, unit)
        : minutes;
    const canSave = !isLocked &&
        lookupsLoaded &&
        !relatedLoading && !relatedError &&
        description.trim().length > 0 &&
        validDuration &&
        (!staffResources || staffResources.some((resource) => resource.bookableresourceid === resourceId)) &&
        (entryType !== "Work" || regardingId !== null);
    const handleSave = async (submit = false) => {
        if (!canSave || saving || submitting)
            return;
        const setBusy = submit ? setSubmitting : setSaving;
        setBusy(true);
        setError(null);
        let changesSaved = false;
        try {
            const startUnchanged = initial &&
                date === format(new Date(initial.msdyn_start), "yyyy-MM-dd") &&
                startTime === format(new Date(initial.msdyn_start), "HH:mm");
            const start = new Date(startUnchanged ? initial.msdyn_start : `${date}T${startTime}:00`);
            const end = new Date(start.getTime() + savedMinutes * 60000);
            let regarding;
            if (regardingId) {
                const display = regardingItems.find((i) => i.value === regardingId)?.label
                    ?? (initial?.regardingobjectid?.id === regardingId ? initial.regardingobjectid.display : "");
                regarding = { type: regardingType, id: regardingId, display };
            }
            const payload = {
                msdyn_description: description.trim(),
                msdyn_start: start.toISOString(),
                msdyn_end: end.toISOString(),
                msdyn_duration: savedMinutes,
                msdyn_type: entryType,
                msdyn_entryStatus: nextStatus,
                bookableresource: initial?.bookableresource ?? resourceId,
                regardingobjectid: regarding,
                msdyn_transactioncategoryid: tcId ?? undefined,
                resourceCategoryId: roleId ?? undefined,
                msdyn_projecttaskid: taskId ?? undefined,
                msdyn_bookableresourcebooking: bookingId ?? undefined,
                // Send the trimmed value verbatim — including empty string when the user
                // cleared the field — so PATCH actually clears the existing Dataverse value
                // instead of skipping the column. (`|| undefined` previously dropped empty
                // strings from the payload, so clearing the field had no effect on save.)
                crb7a_detailsofwork: internalDescription.trim(),
                msdyn_externaldescription: externalComments.trim(),
            };
            let savedId = initial?.msdyn_timeentryid;
            if (initial) {
                if (staffResources) {
                    await adapter.updateStaffTimeEntry(user.id, initial.msdyn_timeentryid, payload);
                }
                else {
                    await adapter.updateTimeEntry(initial.msdyn_timeentryid, payload);
                }
            }
            else {
                const created = staffResources
                    ? await adapter.createStaffTimeEntry(user.id, payload)
                    : await adapter.createTimeEntry(payload);
                savedId = created.msdyn_timeentryid;
            }
            changesSaved = true;
            // "Submit for approval": save the edits above, then move the entry to
            // Submitted through the canonical submit path so the approver sees it
            // again — no separate trip back to the grid. (A Returned entry is saved
            // as Draft first per nextStatus, then submitted.)
            if (submit && savedId) {
                if (staffResources) {
                    await adapter.submitStaffTimeEntry(user.id, savedId, initial?.msdyn_entryStatus === "Returned" || initial?.msdyn_entryStatus === "Submitted");
                }
                else {
                    await adapter.submitEntries([savedId]);
                }
            }
            emitDataEvent("entries-changed");
            onSaved();
            onClose();
        }
        catch (err) {
            console.error("Save time entry failed", err);
            const message = err instanceof Error ? err.message : String(err);
            setError(changesSaved && submit
                ? `Changes were saved as Draft, but submission failed: ${message}. Retry Submit for approval.`
                : message);
            if (changesSaved) {
                emitDataEvent("entries-changed");
                onSaved();
            }
        }
        finally {
            setBusy(false);
        }
    };
    const handleDelete = async () => {
        if (!initial || isLocked || deleting)
            return;
        setDeleting(true);
        try {
            await adapter.deleteTimeEntry(initial.msdyn_timeentryid);
            emitDataEvent("entries-changed");
            onDeleted?.();
            setConfirmDelete(false);
            onClose();
        }
        finally {
            setDeleting(false);
        }
    };
    return (_jsxs(Modal, { open: open, onClose: () => { if (!saving && !submitting && !deleting)
            onClose(); }, icon: _jsx(FileText, { size: 18 }), title: isLocked ? "View entry" : initial ? "Edit entry" : "New time entry", subtitle: isLocked
            ? "This entry has been approved and is read-only."
            : staffResources ? "Record time on behalf of a direct report. Changes are saved as Draft."
                : "Log work against an Account, Work Order, or Project.", size: "lg", footer: _jsxs(_Fragment, { children: [initial && !isLocked && onDeleted && (_jsx(Button, { variant: "danger", onClick: () => setConfirmDelete(true), leftIcon: _jsx(Trash2, { size: 14 }), disabled: saving || deleting, className: "mr-auto", children: "Delete" })), _jsx(Button, { variant: "ghost", onClick: onClose, disabled: saving || submitting || deleting, children: isLocked ? "Close" : "Cancel" }), !isLocked && (_jsx(Button, { variant: canSubmitFromDialog ? "ghost" : undefined, onClick: () => handleSave(false), disabled: !canSave || submitting, loading: saving, leftIcon: _jsx(Plus, { size: 14 }), children: initial ? "Save changes" : "Create entry" })), canSubmitFromDialog && (_jsx(Button, { onClick: () => handleSave(true), disabled: !canSave || saving, loading: submitting, leftIcon: _jsx(Send, { size: 14 }), children: submitLabel }))] }), children: [_jsxs("fieldset", { disabled: saving || submitting || deleting, className: "space-y-4 min-w-0", children: [staffResources && (_jsx(Select, { label: "Employee", value: resourceId, onChange: (event) => setResourceId(event.target.value), disabled: !!initial, options: [
                            { value: "", label: "Select an employee" },
                            ...staffResources.map((resource) => ({ value: resource.bookableresourceid, label: resource.name })),
                        ] })), isLocked && (_jsxs("div", { className: "flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900", children: [_jsx(Lock, { size: 14, className: "mt-0.5 shrink-0" }), _jsxs("div", { children: [_jsx("div", { className: "font-medium", children: "Approved \u2014 cannot be edited" }), _jsx("div", { className: "text-xs text-amber-800 mt-0.5", children: "Ask your approver to return this entry if a change is needed; it will move back to Draft so you can edit and resubmit." })] })] })), !isLocked && (initial?.msdyn_entryStatus === "Submitted" || initial?.msdyn_entryStatus === "Returned") && (_jsx("div", { className: "rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600", children: initial.msdyn_entryStatus === "Submitted" ? (_jsxs(_Fragment, { children: ["Saving will move this entry back to", " ", _jsx("span", { className: "font-medium text-slate-800", children: "Draft" }), " \u2014 your approver will see it again on the next submit.", staffResources && " Use Save and resubmit to keep it in the approval queue after saving."] })) : (_jsxs(_Fragment, { children: ["This entry was ", _jsx("span", { className: "font-medium text-slate-800", children: "returned" }), ". Use", " ", _jsx("span", { className: "font-medium text-slate-800", children: "Resubmit for approval" }), " to save your changes and send it back to your approver, or", " ", _jsx("span", { className: "font-medium text-slate-800", children: "Save changes" }), " to keep it as Draft."] })) })), _jsx(Input, { label: "Name", placeholder: "What did you work on?", value: description, onChange: (e) => setDescription(e.target.value), disabled: isLocked }), _jsxs("div", { className: "grid grid-cols-2 sm:grid-cols-3 gap-3", children: [_jsx(Input, { label: "Date", type: "date", value: date, onChange: (e) => setDate(e.target.value), disabled: isLocked }), _jsx(Input, { label: "Start time", type: "time", value: startTime, onChange: (e) => setStartTime(e.target.value), disabled: isLocked }), _jsx(DurationInput, { label: unit === "Minute" ? "Minutes" : "Hours", unit: unit, minutes: minutes, onChange: (value) => { setMinutes(value); setDurationEdited(true); }, disabled: isLocked })] }), !isLocked && (_jsxs("p", { className: "text-xs text-slate-500", children: [durationRule(unit), " Existing durations are preserved unless changed.", validDuration && savedMinutes !== minutes && ` Will save as ${formatDuration(savedMinutes, unit)}.`] })), error && _jsx("div", { role: "alert", className: "text-sm text-rose-700", children: error }), relatedError && _jsxs("p", { role: "alert", className: "text-sm text-rose-700", children: ["Could not load tasks or bookings: ", relatedError, ". Close and reopen the entry to retry."] }), _jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3", children: [_jsx(Select, { label: "Regarding type", value: regardingType, onChange: (e) => {
                                    setRegardingType(e.target.value);
                                    setRegardingId(null);
                                    setTaskId(null);
                                    setBookingId(null);
                                }, options: regardingOptions, disabled: isLocked }), _jsx(SearchableCombobox, { label: "Regarding", items: regardingItems, value: regardingId, onChange: (id) => { setRegardingId(id); setTaskId(null); setBookingId(null); }, placeholder: regardingType === "account"
                                    ? "Select an account"
                                    : regardingType === "msdyn_workorder"
                                        ? "Select a work order"
                                        : "Select a project", disabled: isLocked })] }), regardingType === "msdyn_project" && regardingId && (_jsx(SearchableCombobox, { label: "Project task", items: tasks.map((t) => ({ value: t.msdyn_projecttaskid, label: t.msdyn_subject })), value: taskId, onChange: setTaskId, placeholder: "Select a task", disabled: isLocked })), regardingType === "msdyn_workorder" && regardingId && (_jsx(SearchableCombobox, { label: "Booking", items: bookings.map((b) => ({ value: b.bookableresourcebookingid, label: b.name })), value: bookingId, onChange: setBookingId, placeholder: "Optional \u2014 link to a scheduled appointment", disabled: isLocked })), _jsxs("div", { className: "grid grid-cols-1 sm:grid-cols-2 gap-3", children: [_jsx(SearchableCombobox, { label: "Service type", items: transCats.map((t) => ({
                                    value: t.msdyn_transactioncategoryid,
                                    label: t.msdyn_description,
                                    hint: rateHint(t),
                                })), value: tcId, onChange: setTcId, placeholder: "Optional", disabled: isLocked }), _jsx(SearchableCombobox, { label: "Role", items: resCats.map((r) => ({
                                    value: r.bookableresourcecategoryid,
                                    label: r.name,
                                    hint: `$${r.hourlyRate}/hr`,
                                })), value: roleId, onChange: setRoleId, placeholder: "Optional \u2014 fallback rate", disabled: isLocked })] }), _jsx(Select, { label: "Type", value: entryType, onChange: (e) => setEntryType(e.target.value), options: typeOptions, disabled: isLocked }), _jsx(Textarea, { label: "Details of Work", hint: "internal notes, not customer-visible", placeholder: "Extended notes for the team (optional)", value: internalDescription, onChange: (e) => setInternalDescription(e.target.value), disabled: isLocked, rows: 3, maxLength: 2000 }), _jsx(Textarea, { label: "External comments", hint: "appears on the customer invoice \u2014 100 character limit", placeholder: "Customer-facing notes that flow onto the invoice (optional)", value: externalComments, onChange: (e) => setExternalComments(e.target.value), disabled: isLocked, rows: 3, maxLength: 100 })] }), _jsx(ConfirmDialog, { open: confirmDelete, onClose: () => setConfirmDelete(false), onConfirm: handleDelete, title: "Delete this time entry?", body: _jsxs(_Fragment, { children: ["This will permanently remove the entry", initial?.msdyn_description ? (_jsxs(_Fragment, { children: [" \u201C", _jsx("span", { className: "font-medium text-slate-900", children: initial.msdyn_description }), "\u201D"] })) : null, ". This action cannot be undone."] }), confirmLabel: "Delete entry", confirmVariant: "danger", loading: deleting })] }));
}
